<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { navigationSections } from "../../constants/navigation";
import { usePermissions } from "../../composables/usePermissions";
import { useAuth } from "../../composables/useAuth";
import { useSubscription } from "../../composables/useSubscription";
import { useCompanyPlan } from "../../composables/useCompanyPlan";
import LogoIconLight from "../../assets/icons/logo_inverted.png";
import { useTheme } from "../../composables/useTheme";
import { isModuleAvailableForPlan } from "@/constants/planModules";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useAppUpdates } from "../../composables/useAppUpdates";
import UpdateModal from "../updates/UpdateModal.vue";
import { useSidebarStore } from "@/stores/sidebar";
import { FirebaseProvider } from "@/infrastructure/providers/FirebaseProvider";
import { doc, getDoc } from "firebase/firestore";
import type { ThemeConfig } from "@/domain/types/configs/company/ThemeConfig";
import { useCompanyStore } from "@/stores/company";
import { useGeneratedAlertsStore } from "@/stores/generatedAlerts";

const props = defineProps<{
  isOpen: boolean;
  currentTheme: "light" | "dark";
}>();

const emit = defineEmits(["toggle", "themeToggled"]);

const router = useRouter();
const { canRead, viewerConfigReady } = usePermissions();
const { currentUser, isLoading: authLoading } = useAuth();
const { toggleTheme } = useTheme();
const { planName, loading: subscriptionLoading } = useSubscription();
const { plan: companyPlan } = useCompanyPlan();
const { t } = useI18n();
const { update, markUpdateAsViewed } = useAppUpdates();
const companyStore = useCompanyStore();
const alertsStore = useGeneratedAlertsStore();

// Agregar el store del sidebar
const sidebarStore = useSidebarStore();

// Configuración de tema personalizada
const themeConfig = ref<ThemeConfig | null>(null);
const loadingTheme = ref(false);

// Computed para logo y nombre personalizados
const customLogoDarkUrl = computed(() => themeConfig.value?.dashboardLogoUrl || '');
const customLogoLightUrl = computed(() =>
  themeConfig.value?.dashboardLogoLightUrl || themeConfig.value?.dashboardLogoUrl || ''
);
const customLogoUrl = computed(() =>
  props.currentTheme === 'light' ? customLogoLightUrl.value : customLogoDarkUrl.value
);
const hasCustomLogo = computed(() => !!themeConfig.value?.dashboardLogoUrl);
const customCompanyName = computed(() => themeConfig.value?.companyName || 'Kila');

// Cargar configuración de tema
const loadThemeConfig = async () => {
  const currentCompanyId = companyStore.selectedCompanyId;
  if (!currentCompanyId) return;
  
  loadingTheme.value = true;
  themeConfig.value = null; // Reset para mostrar Kila mientras carga
  try {
    const db = FirebaseProvider.getInstance().getFirestore();
    const configRef = doc(db, `Companies/${currentCompanyId}/config/THEME_CONFIG`);
    const configSnap = await getDoc(configRef);
    
    if (configSnap.exists()) {
      themeConfig.value = configSnap.data() as ThemeConfig;
    }
  } catch (error) {
    console.error('Error cargando configuración de tema:', error);
  } finally {
    loadingTheme.value = false;
  }
};

// Recargar tema cuando cambie la compañía seleccionada
watch(() => companyStore.selectedCompanyId, () => {
  loadThemeConfig();
});

onMounted(() => {
  loadThemeConfig();
  
  // Escuchar eventos de actualización de tema
  window.addEventListener('theme-config-updated', loadThemeConfig);

  // Activar listener en tiempo real para el badge (el primer snapshot entrega los datos iniciales)
  alertsStore.startRealtimeSync();
});

// Limpiar el listener cuando el componente se destruya
onUnmounted(() => {
  window.removeEventListener('theme-config-updated', loadThemeConfig);
  alertsStore.stopRealtimeSync();
});

// Actualizar el store cuando cambie el prop isOpen
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      sidebarStore.open();
    } else {
      sidebarStore.close();
    }
  },
  { immediate: true }
);

// Modificar el método de toggle para usar el store
const handleToggle = () => {
  emit("toggle");
  sidebarStore.toggle();
};

// Plan de la compañía (Free Premium / Plan Cliente)
const currentPlanName = computed(() => t(`dashboard.billing.plans.${companyPlan.value}.name`));

// Computed para determinar si los datos del menú están cargando
const isMenuLoading = computed(() => {
  // Para un viewer, los módulos del menú dependen de la configuración de solo
  // vista: sin ella el menú se pintaría con los valores por defecto y cambiaría.
  return authLoading.value || subscriptionLoading.value || !currentUser.value || !viewerConfigReady.value;
});

// Método para manejar el cambio de tema
const handleThemeToggle = () => {
  toggleTheme();
  emit("themeToggled");
};

// Exponer el método para el componente padre
defineExpose({ handleThemeToggle });

// Ajustar el ancho del sidebar
const sidebarWidth = computed(() => (props.isOpen ? "w-64" : "w-24"));

// Filtrar las secciones de navegación según los permisos y la compañía seleccionada
const filteredNavigationSections = computed(() => {
  return navigationSections
    .map((section) => ({
      ...section,
      items: section.items
        .filter(
          (item) =>
            canRead(item.module) &&
            isModuleAvailableForPlan(planName.value, item.module)
        ),
    }))
    .filter((section) => section.items.length > 0);
});

const getDynamicBadge = (item: any) => {
  if (item.name === 'Alertas' && alertsStore.unreadCount > 0) {
    return {
      text: alertsStore.unreadCount > 99 ? '99+' : String(alertsStore.unreadCount),
      type: 'error'
    };
  }
  return null;
};

const getBadgeClasses = (type: string) => {
  const baseClasses = "px-2 py-0.5 text-xs font-medium rounded-full";
  const typeClasses: Record<string, string> = {
    primary:
      "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400",
    warning:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    success:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    blue: "text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20",
  };
  return `${baseClasses} ${typeClasses[type]}`;
};

const gotToBilling = () => {
  router.push("/dashboard/billing");
};

// Agregar el computed para las iniciales
const userInitials = computed(() => {
  if (!currentUser.value?.name) return "U";

  const firstInitial = currentUser.value.name.charAt(0);
  const lastInitial = currentUser.value.lastName?.charAt(0) || "";

  return (firstInitial + lastInitial).toUpperCase();
});

const showUpdateModal = ref(false);

// Método para abrir el modal
const openUpdateModal = () => {
  showUpdateModal.value = true;
};

// Método para cerrar el modal
const closeUpdateModal = () => {
  showUpdateModal.value = false;
  // Guardar la versión actual en localStorage cuando se cierra manualmente
  if (update.value) {
    localStorage.setItem("appVersion", update.value.version);
  }
  // Marcar como visto en la base de datos
  markUpdateAsViewed();
};

// Función para determinar si una ruta está activa, priorizando rutas más específicas
const isActiveRoute = (itemRoute: string, sectionItems: any[]) => {
  const currentPath = router.currentRoute.value.path;

  // Obtener todas las rutas de la sección y ordenarlas por longitud (más específicas primero)
  const sectionRoutes = sectionItems.map(item => item.to).sort((a, b) => b.length - a.length);

  // Encontrar la ruta más específica que coincida
  const matchingRoute = sectionRoutes.find(route => currentPath.startsWith(route));

  // Si hay match directo, usar ese
  if (matchingRoute) return matchingRoute === itemRoute;

  // Fallback: match por base de ruta (e.g. /dashboard/reports/* matchea /dashboard/reports/my-reports)
  const itemBase = itemRoute.split('/').slice(0, 3).join('/');
  const currentBase = currentPath.split('/').slice(0, 3).join('/');
  if (itemBase === currentBase) {
    const candidates = sectionRoutes.filter(r => r.startsWith(itemBase));
    return candidates.length > 0 && candidates[0] === itemRoute;
  }

  return false;
};

</script>

<template>
  <!-- Overlay for mobile -->
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
    @click="handleToggle"
  ></div>

  <!-- Sidebar -->
  <aside
    class="fixed inset-y-0 left-0 z-[45] bg-white dark:bg-secondary-800 border-r border-secondary-200 dark:border-secondary-700 transition-all duration-300 flex flex-col lg:translate-x-0"
    :class="[sidebarWidth, { '-translate-x-full': !isOpen }]"
  >
    <!-- Logo y Toggle -->
    <div
      class="flex items-center border-b border-secondary-200 dark:border-secondary-700"
      :class="hasCustomLogo ? 'h-20' : 'h-16'"
    >
      <div class="w-full px-4 flex items-center justify-between">
        <div class="relative flex items-center flex-1 min-w-0">
          <!-- Logo Principal -->
          <RouterLink
            to="/dashboard/tracking-request"
            class="transition-all duration-300 flex"
            :class="{ 'w-0 opacity-0': !isOpen, 'w-full': isOpen }"
          >
            <div class="flex flex-col items-center relative w-full">
              <!-- Sin configuración personalizada: Logo + Texto Kila -->
              <div v-if="!hasCustomLogo" class="flex items-start gap-2 relative">
                <img :src="LogoIconLight" alt="Kila" class="h-8 w-auto relative top-[3px]" />
                <p
                  class="font-kalperica text-2xl mt-1 font-normal tracking-wider"
                  :class="{ 'opacity-0 pointer-events-none': !isOpen }"
                >
                  Kila
                </p>
              </div>
              <!-- Con configuración personalizada: Solo logo -->
              <div v-if="hasCustomLogo" class="flex items-center justify-center w-full">
                <img 
                  :src="customLogoUrl" 
                  :alt="customCompanyName" 
                  class="max-w-full max-h-14 h-auto object-contain" 
                />
              </div>
            </div>
          </RouterLink>

          <!-- Icono Pequeño -->
          <RouterLink
            to="/dashboard/tracking-request"
            class="absolute left-0 transition-all duration-300 w-12"
            :class="{ 'opacity-0 pointer-events-none': isOpen }"
          >
            <div class="flex flex-col gap-2 items-center">
              <!-- Logo pequeño: siempre muestra la imagen (original o personalizada) -->
              <img 
                :src="hasCustomLogo ? customLogoUrl : LogoIconLight" 
                :alt="hasCustomLogo ? customCompanyName.charAt(0) : 'K'" 
                class="h-8 w-auto" 
              />
            </div>
          </RouterLink>
        </div>

        <!-- Toggle Button -->
        <button
          @click="handleToggle"
          class="p-1.5 left-3 relative rounded-lg text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors lg:block"
        >
          <svg
            class="h-4 w-4 transition-transform duration-300"
            :class="{ 'rotate-180': !isOpen }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Navigation -->

    <nav class="flex-1 overflow-y-auto overflow-x-hidden p-4 custom-scrollbar">
      <!-- Version Badge -->
      <div v-if="isOpen" class="mb-6 cursor-pointer" @click="openUpdateModal">
        <div
          class="flex items-center justify-between px-3 py-2 mt-3 rounded-lg bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 border border-primary-200 dark:border-primary-700/30 relative"
        >
          <span
            class="absolute right-2 bottom-9 transition-all duration-300"
            :class="[getBadgeClasses('blue'), { 'right-1 bottom-5': !isOpen }]"
          >
            {{ t("dashboard.sidebar.newUpdate.badge") }}
          </span>
          <div class="flex items-center gap-2">
            <span class="flex h-2 w-2">
              <span
                class="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-primary-400 opacity-75"
              ></span>
              <span
                class="relative inline-flex rounded-full h-2 w-2 bg-primary-500"
              ></span>
            </span>
            <span
              class="text-xs font-medium text-primary-700 dark:text-primary-300"
            >
              {{ t("common.version") }} {{ update?.version }}
            </span>
          </div>
          <div
            class="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 cursor-pointer flex items-center gap-1"
            @click="openUpdateModal"
          >
            <svg
              class="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
      <!-- Minimized Version Badge -->
      <div v-else class="mb-6 flex justify-center">
        <div class="relative group">
          <div
            class="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 border border-primary-200 dark:border-primary-700/30 cursor-pointer"
            @click="openUpdateModal"
          >
            <span
              class="text-xs font-bold text-primary-700 dark:text-primary-300"
              >{{ update?.version }}</span
            >
            <span class="absolute -top-1 -right-1 flex h-2 w-2">
              <span
                class="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-primary-400 opacity-75"
              ></span>
              <span
                class="relative inline-flex rounded-full h-2 w-2 bg-primary-500"
              ></span>
            </span>
          </div>
          <div
            @click="openUpdateModal"
            class="absolute left-1 ml-2 px-2 py-1 bg-secondary-800 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap"
          >
            {{ t("common.version") }} {{ update?.version }}
          </div>
        </div>
      </div>

      <!-- Menu Loading State -->
      <div v-if="isMenuLoading" class="space-y-8">
        <!-- Loading Section 1 -->
        <div class="space-y-2">
          <div
            v-if="isOpen"
            class="px-3 h-4 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse"
          ></div>
          <div class="space-y-1">
            <div
              v-for="n in 3"
              :key="`loading-1-${n}`"
              class="flex items-center gap-x-3 px-3 py-2 rounded-lg"
            >
              <div class="h-5 w-5 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse relative left-2 flex-shrink-0"></div>
              <div
                v-if="isOpen"
                class="h-4 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse flex-1"
              ></div>
            </div>
          </div>
        </div>

        <!-- Loading Section 2 -->
        <div class="space-y-2">
          <div
            v-if="isOpen"
            class="px-3 h-4 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse"
          ></div>
          <div class="space-y-1">
            <div
              v-for="n in 4"
              :key="`loading-2-${n}`"
              class="flex items-center gap-x-3 px-3 py-2 rounded-lg"
            >
              <div class="h-5 w-5 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse relative left-2 flex-shrink-0"></div>
              <div
                v-if="isOpen"
                class="h-4 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse flex-1"
              ></div>
            </div>
          </div>
        </div>

        <!-- Loading Section 3 -->
        <div class="space-y-2">
          <div
            v-if="isOpen"
            class="px-3 h-4 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse"
          ></div>
          <div class="space-y-1">
            <div
              v-for="n in 2"
              :key="`loading-3-${n}`"
              class="flex items-center gap-x-3 px-3 py-2 rounded-lg"
            >
              <div class="h-5 w-5 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse relative left-2 flex-shrink-0"></div>
              <div
                v-if="isOpen"
                class="h-4 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse flex-1"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Menu Loaded State -->
      <div v-else class="space-y-8">
        <div
          v-for="section in filteredNavigationSections"
          :key="section.title"
          class="space-y-2"
        >
          <h3
            v-if="isOpen"
            class="px-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider"
          >
            {{ $t(`dashboard.sidebar.navigation.${section.title}`) }}
          </h3>

          <div class="space-y-1">
            <RouterLink
              v-for="item in section.items"
              :key="item.name"
              :to="item.to"
              class="flex items-center gap-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 group relative"
              :class="[
                isActiveRoute(item.to, section.items)
                  ? 'text-primary-600 dark:text-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'text-secondary-700 dark:text-secondary-300 hover:text-primary-600 dark:hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20',
              ]"
            >
              <div class="relative flex-shrink-0">
                <component
                  :is="item.icon"
                  class="h-5 w-5 relative left-2"
                  :class="[
                    isActiveRoute(item.to, section.items)
                      ? 'text-primary-600 dark:text-primary-500'
                      : 'text-secondary-400 dark:text-secondary-500 group-hover:text-primary-600 dark:group-hover:text-primary-500',
                  ]"
                />
                <!-- Badge dot cuando sidebar está colapsado -->
                <span
                  v-if="!isOpen && getDynamicBadge(item)"
                  class="absolute -top-1 left-5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-800 animate-pulse"
                />
              </div>
              <span
                class="truncate transition-all duration-200 flex-1"
                :class="{ 'opacity-0 w-0': !isOpen }"
              >
                {{ $t(`dashboard.sidebar.navigation.${item.name}`) }}
              </span>
              <!-- Badge con número cuando sidebar está abierto -->
              <span
                v-if="isOpen && getDynamicBadge(item)"
                :class="getBadgeClasses(getDynamicBadge(item)!.type)"
              >
                {{ getDynamicBadge(item)!.text }}
              </span>
            </RouterLink>
          </div>
        </div>
      </div>
    </nav>

    <!-- User Profile -->
    <div class="p-4 border-t border-secondary-200 dark:border-secondary-700">
      <!-- User Profile Loading State -->
      <div
        v-if="isMenuLoading"
        class="flex items-center gap-3 px-3 py-2 rounded-lg"
      >
        <!-- User Avatar Loading -->
        <div class="h-8 w-8 rounded-full bg-secondary-200 dark:bg-secondary-600 animate-pulse"></div>

        <!-- User Info Loading -->
        <div
          v-if="isOpen"
          class="flex-1 space-y-2"
        >
          <div class="h-4 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse w-3/4"></div>
          <div class="h-3 bg-secondary-200 dark:bg-secondary-600 rounded animate-pulse w-1/2"></div>
        </div>
      </div>

      <!-- User Profile Loaded State -->
      <div
        v-else
        class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors cursor-pointer"
      >
        <!-- User Avatar -->
        <div
          @click="gotToBilling"
          class="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-medium text-sm"
        >
          {{ userInitials }}
        </div>

        <!-- User Info -->
        <div
          class="flex-1 transition-all duration-300"
          :class="{ hidden: !isOpen }"
          @click="gotToBilling"
        >
          <div class="flex items-center justify-between">
            <div class="flex flex-col">
              <span
                class="text-sm font-medium text-secondary-900 dark:text-white"
              >
                {{ currentUser?.name }} {{ currentUser?.lastName }}
              </span>
              <span class="text-xs text-secondary-500 dark:text-secondary-400">
                {{ $t("dashboard.sidebar.userProfile.currentPlan") }}
                {{ currentPlanName }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </aside>

  <UpdateModal
    v-if="update"
    :is-open="showUpdateModal"
    :update="update"
    @close="closeUpdateModal"
  />
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.3);
  border-radius: 3px;
  transition: background-color 0.2s;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.5);
}

/* Estilos para dark mode */
:deep(.dark) .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
}

:deep(.dark) .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

/* Asegurar que las transiciones sean suaves */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Ajustar el z-index para el icono cuando el sidebar está cerrado */
img.absolute {
  z-index: 10;
}
</style>
